import React from 'react';
import { CURRENCIES, getCurrencySymbol } from '../../../../shared/constants/billing.js';

export function SendQuoteModal({ show, app, activeRequest, quoteAmount, quoteCurrency, monthlyPrice, quoteNote, depositPercentage = 20, onDepositPercentageChange, submitting, onClose, onSubmit, onAmountChange, onCurrencyChange, onMonthlyPriceChange, onNoteChange }) {
  const appData = app;
  if (!show || !appData) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Send Quote</h3>
        
        <p className="text-gray-600 mb-4">
          App: <strong>{appData.name}</strong>
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            {appData?.preferredCurrency ? (
              <>
                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                  {(() => {
                    const currencyCode = (quoteCurrency || 'usd').toLowerCase();
                    const currency = CURRENCIES.find(c => c.code === currencyCode);
                    return (
                      <span className="font-medium">
                        {currencyCode.toUpperCase()} - {currency?.name || currencyCode.toUpperCase()}
                      </span>
                    );
                  })()}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Currency is set by the user and cannot be changed
                </p>
              </>
            ) : (
              <>
                <select
                  value={quoteCurrency || 'usd'}
                  onChange={(e) => onCurrencyChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code.toUpperCase()} - {currency.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select currency for this quote
                </p>
              </>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Development Cost <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500 font-medium">
                {getCurrencySymbol(quoteCurrency)}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={quoteAmount}
                onChange={(e) => onAmountChange(e.target.value)}
                placeholder="500.00"
                className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deposit
            </label>
            <select
              value={depositPercentage}
              onChange={(e) => onDepositPercentageChange && onDepositPercentageChange(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value={0}>0% — Full payment on approval</option>
              <option value={20}>20% deposit / 80% final</option>
              <option value={50}>50% deposit / 50% final</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {depositPercentage === 0 ? 'Client pays the full amount when they approve the quote' : `Client pays ${depositPercentage}% now, rest when work is complete`}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Monthly Subscription Price <span className="text-red-500">*</span>
            </label>
            {appData.monthlyPrice && (
              <div className="mb-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                Current: {getCurrencySymbol(quoteCurrency)}{parseFloat(appData.monthlyPrice).toFixed(2)}/month
              </div>
            )}
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500 font-medium">
                {getCurrencySymbol(quoteCurrency)}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={monthlyPrice}
                onChange={(e) => onMonthlyPriceChange(e.target.value)}
                placeholder="5.00"
                className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (Optional)
            </label>
            <textarea
              value={quoteNote || ''}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Add a note to include with the quote. This will be included in the email and saved in the app's notes section."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              This note will be included in the quote email and saved in the app's notes section.
            </p>
          </div>
        </div>

        {/* Payment Flow Explanation */}
        {quoteAmount && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 What happens after sending this quote:</h4>
            <div className="text-xs text-blue-800 space-y-1">
              {parseFloat(quoteAmount) === 0 ? (
                <>
                  {parseFloat(monthlyPrice) > 0 ? (
                    <>
                      <p>• <strong>£0 quote with monthly fee:</strong> User will be asked to set up a payment method (no charge now)</p>
                      <p>• Once payment method is set up, deposit and final payment are automatically marked as paid</p>
                      <p>• Monthly billing starts when the client completes payment</p>
                    </>
                  ) : (
                    <p>• <strong>£0 quote:</strong> Quote will be auto-approved immediately (no payment needed)</p>
                  )}
                </>
              ) : (() => {
                const pct = depositPercentage || 20;
                const dep = (parseFloat(quoteAmount) * pct / 100).toFixed(2);
                const fin = (parseFloat(quoteAmount) * (100 - pct) / 100).toFixed(2);
                return pct === 0 ? (
                  <>
                    <p>• User will pay <strong>{getCurrencySymbol(quoteCurrency)}{parseFloat(quoteAmount).toFixed(2)} full amount</strong> when they approve</p>
                    {parseFloat(monthlyPrice) > 0 && (
                      <p>• Monthly billing of <strong>{getCurrencySymbol(quoteCurrency)}{parseFloat(monthlyPrice).toFixed(2)}/month</strong> via Stripe subscription</p>
                    )}
                  </>
                ) : (
                  <>
                    <p>• User will pay <strong>{getCurrencySymbol(quoteCurrency)}{dep} deposit ({pct}%)</strong> to start work</p>
                    <p>• User will pay <strong>{getCurrencySymbol(quoteCurrency)}{fin} final payment ({100 - pct}%)</strong> when work is complete</p>
                    {parseFloat(monthlyPrice) > 0 && (
                      <p>• Monthly billing of <strong>{getCurrencySymbol(quoteCurrency)}{parseFloat(monthlyPrice).toFixed(2)}/month</strong> starts when the client completes payment</p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={quoteAmount === '' || quoteAmount === null || quoteAmount === undefined || parseFloat(quoteAmount) < 0 || !monthlyPrice || parseFloat(monthlyPrice) < 0 || submitting}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Sending...' : '📋 Send Quote'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function GoLiveModal({ show, app, productionUrl, submitting, onClose, onSubmit, onProductionUrlChange }) {
  const appData = app;
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-gray-900 mb-4">🚀 Go Live</h3>
        
        <p className="text-gray-600 mb-4">
          Mark <strong>{appData?.name}</strong> as complete and go live?
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Production URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={productionUrl}
            onChange={(e) => onProductionUrlChange(e.target.value)}
            placeholder="https://your-app.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          />
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={!productionUrl.trim() || submitting}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Going Live...' : '🚀 Go Live'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SendForApprovalModal({ show, app, submitting, onClose, onSubmit }) {
  const appData = app;
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-gray-900 mb-4">📤 Send for Approval</h3>
        
        <p className="text-gray-600 mb-4">
          Send <strong>{appData?.name}</strong> to the customer for approval?
        </p>
        
        <p className="text-sm text-gray-500 mb-6">
          The customer will receive an email with a preview link and can approve or request changes.
        </p>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Sending...' : '📤 Send for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function StatusChangeModal({ show, app, newStatus, submitting, onClose, onSubmit }) {
  const appData = app;
  if (!show) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !submitting) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-gray-900 mb-4">⚠️ Change App Status</h3>
        
        <p className="text-gray-600 mb-4">
          Are you sure you want to change <strong>{appData?.name}</strong> status from{' '}
          <span className="font-semibold">{appData?.status}</span> to{' '}
          <span className="font-semibold">{newStatus}</span>?
        </p>
        
        {newStatus === 'live' && appData?.status !== 'live' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> Setting an app to "live" will make it accessible.
              {!appData?.appUrl && (
                <span className="block mt-1">⚠️ This app does not have a production URL set.</span>
              )}
            </p>
          </div>
        )}
        
        {newStatus === 'archived' && appData?.status !== 'archived' && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-orange-800">
              <strong>Warning:</strong> Archiving this app will make it inaccessible.
            </p>
          </div>
        )}
        
        {newStatus === 'draft' && appData?.status === 'live' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Setting a live app back to "draft" will hide it.
            </p>
          </div>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSubmit();
            }}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Updating...' : 'Confirm Change'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DeclineQuoteModal({ show, app, note, submitting, onClose, onSubmit, onNoteChange }) {
  if (!show || !app) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !submitting) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-gray-900 mb-4">❌ Decline Quote</h3>
        
        <p className="text-gray-600 mb-4">
          Are you sure you want to decline providing a quote for <strong>{app.name}</strong>?
        </p>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> This will remove the app from the quotes-needed list. The user will no longer see "Quote Requested" status.
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message to User (Optional)
          </label>
          <textarea
            value={note || ''}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Add a note explaining why you're declining this quote. This will be included in the email sent to the user."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            The user will receive an email notifying them that the quote has been declined. If you add a message above, it will be included in that email and also saved as a note on their app.
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSubmit();
            }}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Declining...' : '❌ Decline Quote'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DeleteAppModal({ show, app, confirmText, submitting, onClose, onSubmit, onConfirmTextChange }) {
  if (!show || !app) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !submitting) {
      onClose();
    }
  };

  const isConfirmed = confirmText === app.name;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-red-600 mb-4">🗑️ Delete App Permanently</h3>
        
        <p className="text-gray-600 mb-4">
          Are you sure you want to permanently delete <strong>{app.name}</strong>?
        </p>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-800">
            <strong>⚠️ Warning:</strong> This action cannot be undone. The following will be permanently deleted:
          </p>
          <ul className="text-sm text-red-700 mt-2 list-disc list-inside space-y-1">
            <li>All app data and settings</li>
            <li>All work history</li>
            <li>All notes (customer and admin)</li>
            <li>All charges and billing records</li>
            <li>All linked repositories</li>
          </ul>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type <strong className="text-red-600">{app.name}</strong> to confirm:
          </label>
          <input
            type="text"
            value={confirmText || ''}
            onChange={(e) => onConfirmTextChange(e.target.value)}
            placeholder={app.name}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            autoComplete="off"
          />
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSubmit();
            }}
            disabled={!isConfirmed || submitting}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Deleting...' : '🗑️ Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SendPaymentLinkModal({ show, app, amount, monthlyFee, onSubmit, onClose, onAmountChange, onMonthlyFeeChange, submitting, createdResult, onDone, onCopyLink }) {
  if (!show || !app) return null;

  const appData = app;

  if (createdResult?.checkoutUrl) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 max-w-lg w-full">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment link ready</h2>
          <p className="text-gray-600 mb-4">
            {createdResult.emailSent
              ? `Email sent to ${createdResult.userEmail || 'the app owner'}.`
              : 'Share the payment link below with your client.'}
          </p>
          <div className="flex gap-2 mb-6">
            <input
              type="text"
              readOnly
              value={createdResult.checkoutUrl}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono truncate"
            />
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(createdResult.checkoutUrl);
                onCopyLink?.();
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium whitespace-nowrap"
            >
              Copy Link
            </button>
            <a
              href={createdResult.checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium whitespace-nowrap"
            >
              Open
            </a>
          </div>
          <button
            type="button"
            onClick={onDone}
            className="w-full px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-gray-900 mb-4">💳 Send Payment Link</h3>
        <p className="text-gray-600 mb-4">
          Send a direct payment link to <strong>{appData.name}</strong>. No quote step — client pays when they click.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => onAmountChange(e.target.value)}
              placeholder="500.00"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Fee ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={monthlyFee}
              onChange={(e) => onMonthlyFeeChange(e.target.value)}
              placeholder="0.00"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <p className="text-xs text-gray-500">
            At least one must be greater than 0. Client receives an email with the Stripe payment link.
          </p>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting || (parseFloat(amount || 0) <= 0 && parseFloat(monthlyFee || 0) <= 0)}
            className="flex-1 px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50 font-medium"
          >
            {submitting ? 'Sending...' : 'Send Link'}
          </button>
        </div>
      </div>
    </div>
  );
}

